import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionLoader } from "@/components/common";
import { Printer, Download, Loader2, Info } from "lucide-react";

/**
 * NameTagPrinter — shared printable name-badge tool. Reused by the Studio
 * "Name badges" tab and the Manage → QR area. Each member's badge shows only the
 * fields they've enabled in My Card (title / company / contact).
 */
export function NameTagPrinter() {
  const [summary, setSummary] = useState(null);
  const [scope, setScope] = useState("all");
  const [size, setSize] = useState("name_tag");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/cards/badges/summary")
      .then((r) => setSummary(r.data))
      .catch(() => { toast.error("Could not load badge options"); setSummary({ total_members: 0, events: [] }); });
  }, []);

  if (!summary) return <SectionLoader label="Loading…" />;

  const selectedEvent = summary.events.find((e) => e.event_id === scope);
  const count = scope === "all" ? summary.total_members : (selectedEvent?.confirmed || 0);
  const perPage = size === "name_tag" ? 8 : 10;
  const pages = Math.max(1, Math.ceil(count / perPage));

  const download = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ size });
      if (scope !== "all") params.set("event_id", scope);
      const res = await api.get(`/cards/badges.pdf?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `friday-badges-${size}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Badge sheet downloaded");
    } catch (e) {
      toast.error("Could not generate the badge sheet");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="border-border bg-card">
      <CardContent className="space-y-5 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary"><Printer className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-semibold text-foreground">Printable name badges</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">Print a sheet of member badges — each with a QR people can scan to connect. Perfect for the welcome table.</p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Who to print</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="mt-1.5" data-testid="badges-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members ({summary.total_members})</SelectItem>
                {summary.events.map((e) => (
                  <SelectItem key={e.event_id} value={e.event_id}>{e.title} · {e.confirmed} going</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Badge size</Label>
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger className="mt-1.5" data-testid="badges-size-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name_tag">Name badge — 8 per page</SelectItem>
                <SelectItem value="business_card">Business card — 10 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-border bg-secondary/40 p-4 sm:flex-row sm:items-center">
          <p className="text-sm text-muted-foreground" data-testid="badges-count">
            {count} badge{count === 1 ? "" : "s"} · {pages} page{pages === 1 ? "" : "s"} (US Letter)
          </p>
          <Button onClick={download} disabled={downloading || count === 0} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="badges-download-button">
            {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Download PDF
          </Button>
        </div>

        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Each member controls which details (title, company, contact) appear on their badge from <span className="font-medium text-foreground">My Card → Name badge &amp; on-screen plate</span>.
        </p>
      </CardContent>
    </Card>
  );
}

export default NameTagPrinter;
